import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { type FormState, useFormContext } from 'react-hook-form';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useFormSectionTiming, {
  type SectionMs,
} from '~hooks/useAnalytics/useFormSectionTiming';
import type { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import type { MaterialRateDetailsHandler } from '~pages/Sales/Orders/NewOrderForm/MaterialRateDetails';
import type { RateDetailsDTO } from '~pages/Sales/Orders/NewOrderForm/schema';
import { deriveDriverRateFromVendorRate } from '~pages/Sales/Orders/NewOrderForm/utils';
import type { OrderFormHandle } from '~pages/Sales/Orders/orderFormHandle';
import type { FreightRatesHandler } from '~pages/Sales/Projects/components/FreightRates';
import type { SiteRateScheduleOperations } from '~pages/Sales/Projects/components/SiteRatesForm';
import { checkRateTypeMismatch } from '~utils/rateValidation';

import { type OrderFormV3FieldValues, useOrderFormV3Context } from './OrderFormV3Context';
import { ContactsPanel } from './panels/ContactsPanel';
import { DateAndTimePanel } from './panels/DateAndTimePanel';
import { DeliveryEstimatesPanel } from './panels/DeliveryEstimatesPanel';
import { FileAttachmentsPanel } from './panels/FileAttachmentsPanel';
import { LocationDetailsPanel } from './panels/LocationDetailsPanel';
import { MaterialsPanel } from './panels/MaterialsPanel';
import { NotesPanel } from './panels/NotesPanel';
import { OrderCustomerDetailsPanel } from './panels/OrderCustomerDetailsPanel';
import { ProjectAttachmentsPanel } from './panels/ProjectAttachmentsPanel';
import { ProjectDetailsPanel } from './panels/ProjectDetailsPanel';
import { RateDetailsPanel } from './panels/RateDetailsPanel';
import { RequesterPanel } from './panels/RequesterPanel';
import { TaxPanel } from './panels/TaxPanel';
import { TrucksPanel } from './panels/TrucksPanel';

/**
 * The extraction payload FreightRates hands back at submit time. Extracted
 * structurally — the handler does not export it piecemeal — and widened so
 * `siteRateScheduleOperations` may be absent, mirroring the legacy
 * no-handler fallback.
 */
type FreightRateExtract = Omit<
  Parameters<Parameters<NonNullable<FreightRatesHandler['onSubmit']>>[0]>[0],
  'siteRateScheduleOperations'
> & {
  siteRateScheduleOperations: SiteRateScheduleOperations | undefined;
};

/** MaterialRateDetails' DTO is unexported — extract it from its handler. */
type MaterialRateExtract = Awaited<ReturnType<MaterialRateDetailsHandler['submit']>>;

/**
 * The freight-rate block merged over the form data. TRE-14094: each rate
 * triplet appears ONLY when that rate was explicitly modified — omitted keys
 * keep the deparse layer from clobbering Site Rate Schedule cascade-written
 * values with nulls.
 */
type FreightRateFields = {
  customerRate?: RateDetailsDTO;
  customerRateType?: RateDetailsDTO['targetRateType'];
  customerRateValue?: RateDetailsDTO['targetRateValue'];
  vendorRate?: RateDetailsDTO;
  vendorRateType?: RateDetailsDTO['targetRateType'];
  vendorRateValue?: RateDetailsDTO['targetRateValue'];
  driverRate?: RateDetailsDTO;
  driverRateType?: RateDetailsDTO['targetRateType'];
  driverRateValue?: RateDetailsDTO['targetRateValue'];
  customerAddOnAssignments: AddOnAssignmentSchema[];
  vendorAddOnAssignments: AddOnAssignmentSchema[];
  driverAddOnAssignments: AddOnAssignmentSchema[];
  siteRateScheduleOperations: SiteRateScheduleOperations | undefined;
};

/**
 * What the submit callback receives: the validated form data with the
 * imperative side-channels merged over it. Legacy assembles the same shape
 * into an `unknown` callback arg — v3 types it; deparseV3 consumes it.
 * Overridden keys lose their schema-field types because the extracts win,
 * exactly as the legacy spread order dictates.
 */
export type OrderFormV3SubmitPayload = Omit<
  OrderFormV3FieldValues,
  | keyof FreightRateFields
  | 'materialRate'
  | 'materialRateUoM'
  | 'materialRateBuyPrice'
  | 'materialRateSellPrice'
  | 'defaultSiteRateScheduleId'
> &
  FreightRateFields & {
    materialRate: MaterialRateExtract['materialRate'] | undefined;
    materialRateUoM: MaterialRateExtract['materialRateUoM'] | undefined;
    materialRateBuyPrice: MaterialRateExtract['materialRateBuyPrice'] | undefined;
    materialRateSellPrice: MaterialRateExtract['materialRateSellPrice'] | undefined;
    defaultSiteRateScheduleId: string | null;
  };

/**
 * Imperative handle exposed by the v3 shell: the eight legacy
 * OrderFormHandle methods (assignability is pinned by the shell test's
 * expectTypeOf) with `submit`'s callback arg typed (legacy declares it
 * arg-less and OrderForm.tsx consumed the payload under @ts-ignore), plus
 * `getDirtyFields()` so the dialog can hand RHF's dirty snapshot to
 * deparseUpdateV3 at save time (the same pattern ProjectFormV2Handle uses),
 * and the section-timing controls the dialog reads into `section_ms`.
 * Legacy NewOrderForm need not provide the extension; the dialog
 * optional-chains and falls back to a full-coverage `{}` only on the v3 path.
 */
export type OrderFormV3Handle = Omit<OrderFormHandle, 'submit'> & {
  submit: (callBack?: (payload: OrderFormV3SubmitPayload) => void) => Promise<void>;
  getDirtyFields: () => FormState<OrderFormV3FieldValues>['dirtyFields'];
  getSectionMs: () => SectionMs;
  pauseSectionTiming: () => void;
  resetSectionTiming: () => void;
};

const RATE_TYPE_VALUES: readonly RateType[] = Object.values(RateType);

/**
 * The freight extract carries the rate type as the autocomplete option's
 * string `value`; legacy casts it to the enum — v3 narrows instead. A value
 * outside the enum means "nothing to compare", which the mismatch check
 * already treats as no-warning.
 */
function toRateType(value: string | null | undefined): RateType | null {
  if (!value) {
    return null;
  }
  return RATE_TYPE_VALUES.find((rateType) => String(rateType) === value) ?? null;
}

/**
 * Legacy fallback when no FreightRates handler is mounted: no rates, empty
 * add-on lists, no SRS operations. TrucksPanel registers the real handler;
 * this covers the pre-mount window exactly like legacy.
 */
const EMPTY_FREIGHT_RATE_EXTRACT: FreightRateExtract = {
  customerRate: undefined,
  vendorRate: undefined,
  driverRate: undefined,
  customerRateAddOnAssignments: [],
  vendorRateAddOnAssignments: [],
  driverRateAddOnAssignments: [],
  siteRateScheduleOperations: undefined,
};

/**
 * Thin shell over the form the Provider owns: renders the (still
 * empty) panel surface plus the rate-type mismatch dialog, and implements ALL
 * EIGHT OrderFormHandle methods with the exact legacy signatures
 * so OrderForm.tsx's single formRef serves legacy and v3 interchangeably.
 * No data fetching, no API calls, no flag reads — those belong to the
 * Provider and to OrderForm.tsx.
 */
export const OrderFormV3 = forwardRef<OrderFormV3Handle>(
  function OrderFormV3(_props, ref) {
    const { formState, getValues, handleSubmit, reset, setError } =
      useFormContext<OrderFormV3FieldValues>();

    // Subscribe to `dirtyFields` during render so the handle's getDirtyFields()
    // read below is accurate. RHF's formState is a lazy proxy: an unsubscribed
    // `dirtyFields` can stay frozen at `{}`, and a handle that only read it at
    // call time would hand deparseUpdateV3 an empty snapshot — turning every
    // edit save into a no-op PATCH (the same defensive subscription
    // ProjectFormV2.tsx uses, load-bearing here for the same reason it is
    // there).
    const { dirtyFields } = formState;
    const {
      callbacks,
      defaultOrder,
      defaultSiteRateScheduleId,
      fileAttachmentsRef,
      freightRatesRef,
      freightRatesSectionRef,
      isCopyAndEdit,
      materialRateDetailsRef,
      pendingSrsOpsRef,
      pushErrorAlert,
      useVendorRateForDriver,
    } = useOrderFormV3Context();

    const isEditingOrder = Boolean(defaultOrder?.id);
    const {
      focusSection,
      getSectionMs,
      pauseSectionTiming,
      pauseWhenFocusLeavesForm,
      resetSectionTiming,
    } = useFormSectionTiming('order_form_v3', isEditingOrder && !isCopyAndEdit);

    // Rate-type mismatch interception: the dialog pauses the submission;
    // Confirm resumes it, Return-to-form discards it.
    const [showRateTypeMismatchWarning, setShowRateTypeMismatchWarning] = useState(false);
    const pendingSubmitCallbackRef = useRef<(() => void) | null>(null);

    // Only one assembly at a time — a re-entrant submit() while the previous
    // one is still assembling is dropped, so handleSubmit can never run twice
    // for one user action. Released in the finally below; on the mismatch path
    // the paused submission belongs to the dialog from then on.
    const submitInFlightRef = useRef(false);

    const submit = async (
      callBack?: (payload: OrderFormV3SubmitPayload) => void,
    ): Promise<void> => {
      if (submitInFlightRef.current) {
        return;
      }
      submitInFlightRef.current = true;
      try {
        // The ENTIRE assembly runs inside handleSubmit's onValid, exactly
        // once. The ordering is load-bearing (TRE-15128): step 4 below calls
        // MaterialRateDetails' submit(), which runs THIS form's handleSubmit
        // and resolves its Promise ONLY on a valid form (it has no onInvalid
        // branch). Extracting before validating means that on an invalid form
        // the Promise never resolves and the whole submit hangs silently.
        // Keeping the assembly inside onValid runs it only when the form is
        // valid; an invalid submit takes onInvalid, which reports the field
        // errors instead of stalling. (Legacy did exactly this — the assembly
        // lived inside handleSubmit's onValid; the v3 split had hoisted the
        // extraction ahead of validation.)
        await handleSubmit(
          async (formData) => {
            // 1. Freight-rate extraction. Handler contract: resolves true
            //    after the callback got the extract, false when freight
            //    validation failed — the callback never fires then, so the
            //    submission aborts.
            let extracted: FreightRateExtract | null = null;
            const freightRatesOnSubmit = freightRatesRef.current?.onSubmit;
            if (freightRatesOnSubmit) {
              const isFreightRatesValid = await freightRatesOnSubmit((data) => {
                extracted = data;
              });
              if (!isFreightRatesValid) {
                AnalyticsController.track('order_form_v3_validation_failed', {
                  reason: 'freight_rates',
                });
                pushErrorAlert(t('form_validation_errors.freight_rates_have_errors'));
                freightRatesSectionRef.current?.scrollIntoView?.({
                  behavior: 'smooth',
                  block: 'center',
                });
                return;
              }
            }
            const freightRateDetails = extracted ?? EMPTY_FREIGHT_RATE_EXTRACT;

            // 2. Vendor→driver derivation: creating only, and only while the
            //    vendor-for-driver default is on.
            const shouldDeriveDriverRate = useVendorRateForDriver && !isEditingOrder;
            const derivedDriverRate = shouldDeriveDriverRate
              ? deriveDriverRateFromVendorRate(freightRateDetails.vendorRate)
              : freightRateDetails.driverRate;
            const derivedDriverAddOnAssignments = shouldDeriveDriverRate
              ? freightRateDetails.vendorRateAddOnAssignments
              : freightRateDetails.driverRateAddOnAssignments;

            // 3. Conditional spreads — see FreightRateFields above for why
            //    omitted keys matter.
            const freightRateFields: FreightRateFields = {
              ...(freightRateDetails.customerRate
                ? {
                    customerRate: freightRateDetails.customerRate,
                    customerRateType: freightRateDetails.customerRate.targetRateType,
                    customerRateValue: freightRateDetails.customerRate.targetRateValue,
                  }
                : {}),
              ...(freightRateDetails.vendorRate
                ? {
                    vendorRate: freightRateDetails.vendorRate,
                    vendorRateType: freightRateDetails.vendorRate.targetRateType,
                    vendorRateValue: freightRateDetails.vendorRate.targetRateValue,
                  }
                : {}),
              ...(derivedDriverRate
                ? {
                    driverRate: derivedDriverRate,
                    driverRateType: derivedDriverRate.targetRateType,
                    driverRateValue: derivedDriverRate.targetRateValue,
                  }
                : {}),
              customerAddOnAssignments: freightRateDetails.customerRateAddOnAssignments,
              vendorAddOnAssignments: freightRateDetails.vendorRateAddOnAssignments,
              driverAddOnAssignments: derivedDriverAddOnAssignments,
              siteRateScheduleOperations: freightRateDetails.siteRateScheduleOperations,
            };

            // 4. Material-rate extraction — safe inside onValid: the form is
            //    valid, so the embed's internal handleSubmit resolves.
            const materialRateDetails = await materialRateDetailsRef.current?.submit();

            // 5. Stash SRS operations for OrderForm.tsx to commit AFTER the
            //    order save: the order endpoint variant needs an order id to
            //    attach to.
            pendingSrsOpsRef.current =
              freightRateDetails.siteRateScheduleOperations ?? null;

            // 6. Merge the validated form data with the side-channels.
            const finalizeSubmission = () => {
              callBack?.({
                ...formData,
                materialRate: materialRateDetails?.materialRate,
                materialRateUoM: materialRateDetails?.materialRateUoM,
                materialRateBuyPrice: materialRateDetails?.materialRateBuyPrice,
                materialRateSellPrice: materialRateDetails?.materialRateSellPrice,
                ...freightRateFields,
                defaultSiteRateScheduleId,
              });
            };

            // 7. Rate-type mismatch interception: the dialog pauses the merged
            //    submission; Confirm resumes it, Return-to-form discards it. No
            //    re-validation — we are already in the valid path.
            const hasMismatch = checkRateTypeMismatch(
              toRateType(freightRateFields.customerRateType?.value),
              materialRateDetails?.materialRateUoM?.id,
              getValues('materialRateItemized'),
            );

            if (hasMismatch) {
              pendingSubmitCallbackRef.current = finalizeSubmission;
              setShowRateTypeMismatchWarning(true);
            } else {
              finalizeSubmission();
            }
          },
          () => {
            AnalyticsController.track('order_form_v3_validation_failed', {
              reason: 'schema',
            });
          },
        )();
      } finally {
        submitInFlightRef.current = false;
      }
    };

    const resetForm: OrderFormHandle['resetForm'] = (callBack) => {
      reset();
      callBack?.();
    };

    // File-attachment lifecycle: delegates to the embed's handler through the
    // context ref, populated by FileAttachmentsPanel at mount — flushing the
    // upload queue on save and rolling back session-created attachments on
    // close.
    const fileAttachmentsOnSubmit: OrderFormHandle['fileAttachmentsOnSubmit'] = async (
      orderId,
    ) => {
      await fileAttachmentsRef.current?.onSubmit(orderId);
    };

    const fileAttachmentsOnClose: OrderFormHandle['fileAttachmentsOnClose'] =
      async () => {
        await fileAttachmentsRef.current?.onCancel();
      };

    const setFieldError: OrderFormHandle['setFieldError'] = (field, message) => {
      setError(field, { message });
    };

    // ALL EIGHT OrderFormHandle methods plus the documented getDirtyFields and
    // section-timing extensions (OrderFormV3Handle above) — legacy-interface
    // assignability is pinned by the shell test's expectTypeOf. Like legacy,
    // the handle refreshes every render so no closure goes stale.
    useImperativeHandle(ref, () => ({
      submit,
      updateProjectDefaultSites: callbacks.updateProjectDefaultSites,
      resetForm,
      fileAttachmentsOnSubmit,
      fileAttachmentsOnClose,
      setFieldError,
      takePendingSiteRateScheduleOperations:
        callbacks.takePendingSiteRateScheduleOperations,
      applySiteRateSaveResult: callbacks.applySiteRateSaveResult,
      // RHF dirtyFields snapshot at call time — the save path gates the sparse
      // PATCH body on it (deparseUpdateV3). Truthiness is the only contract:
      // primitives mark booleans, object/array fields nested maps.
      getDirtyFields: () => dirtyFields,
      getSectionMs,
      pauseSectionTiming,
      resetSectionTiming,
    }));

    // Root carries the shared `order-form` test-id (NOT `order-form-v3`) so the
    // e2e suite's order-form selectors are form-agnostic — the same spec passes
    // against legacy and v3. v3-specific anchors are the columns and fields
    // below (`order-form-v3-*`).
    return (
      <Box
        data-test-id="order-form"
        onBlur={pauseWhenFocusLeavesForm}
        onFocus={(event) => focusSection(null, event)}
        sx={{ mt: -1 }}
      >
        {/* Two-column layout mirroring the legacy form.
          All 14 panels are live — the column test-ids anchor the
          ordered-inventory assertion in __tests__/OrderFormV3.test.tsx. */}
        <Grid container spacing={2} sx={{ mb: 1, mt: 3 }}>
          {/* LEFT column — eight panels. */}
          <Grid size={{ xs: 6 }} data-test-id="order-form-v3-left-column">
            <OrderCustomerDetailsPanel />
            <DateAndTimePanel />
            <Box onFocus={(event) => focusSection('materials', event)}>
              <MaterialsPanel />
            </Box>
            <Box
              onFocus={(event) =>
                focusSection(
                  freightRatesSectionRef.current?.contains(event.target)
                    ? 'freight_rates'
                    : 'trucks',
                  event,
                )
              }
            >
              <TrucksPanel />
            </Box>
            <TaxPanel />
            <RateDetailsPanel />
            <DeliveryEstimatesPanel />
            <NotesPanel />
          </Grid>
          {/* RIGHT column — six panels. */}
          <Grid size={{ xs: 6 }} data-test-id="order-form-v3-right-column">
            <ProjectDetailsPanel />
            <Box onFocus={(event) => focusSection('location', event)}>
              <LocationDetailsPanel />
            </Box>
            <Box onFocus={(event) => focusSection('contacts', event)}>
              <ContactsPanel />
            </Box>
            <FileAttachmentsPanel />
            <ProjectAttachmentsPanel />
            <RequesterPanel />
          </Grid>
        </Grid>

        {/* Rate-type mismatch warning — mirrors the legacy form's dialog. */}
        <ConfirmationDialog
          isOpen={showRateTypeMismatchWarning}
          onClose={() => {
            setShowRateTypeMismatchWarning(false);
            pendingSubmitCallbackRef.current = null;
          }}
          onConfirm={() => {
            setShowRateTypeMismatchWarning(false);
            pendingSubmitCallbackRef.current?.();
            pendingSubmitCallbackRef.current = null;
          }}
          title={t('form_validation_errors.rate_type_mismatch_warning_title')}
          cancelButtonProps={{ children: t('actions.return_to_form') }}
          confirmButtonProps={{ children: t('actions.continue_anyway') }}
        >
          <Typography>
            {t('form_validation_errors.rate_type_mismatch_warning_message')}
          </Typography>
        </ConfirmationDialog>
      </Box>
    );
  },
);
